/**
 * Implementación del repositorio de Quote usando HTTP
 * Esta es la capa de infraestructura que se comunica con la API
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@env';
import { QuoteRepository } from '@domain/repositories/quote.repository';
import {
  Quote,
  QuoteOffers,
  OfferDetail,
  WorkType,
  ScheduleOption
} from '@domain/models/quote.model';
import {
  QuotePayloadDto,
  QuoteResponseDto,
  QuoteOffersResponseDto,
  OfferDetailDto,
  WorkTypeDto,
  ScheduleOptionDto,
  QuoteSummaryDto
} from '@infra/dto/quote.dto';
import {
  QuoteMapper,
  WorkTypeMapper,
  ScheduleOptionMapper
} from '@infra/mappers/quote.mapper';

@Injectable({ providedIn: 'root' })
export class QuoteRepositoryImpl implements QuoteRepository {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/v1/quotes`;

  create(quote: Quote): Observable<Quote> {
    const payload = QuoteMapper.toDto(quote);
    return this.http.post<QuoteResponseDto>(`${this.baseUrl}/`, payload).pipe(
      map(response => QuoteMapper.toDomain(response))
    );
  }

  getOffers(quoteId: string, page = 1, pageSize = 10): Observable<QuoteOffers> {
    const url = `${this.baseUrl}/${quoteId}/offers`;
    const params = { page: page.toString(), pageSize: pageSize.toString() };
    return this.http.get<QuoteOffersResponseDto>(url, { params }).pipe(
      map(response => {
        // Mapear a dominio pero preservar campos adicionales del DTO
        const domainOffers = QuoteMapper.offersToDomain(response);
        // Agregar campos adicionales que vienen del backend pero no están en el modelo de dominio
        domainOffers.completed.items = domainOffers.completed.items.map((item, idx) => ({
          ...item,
          ...(response.completed.items[idx] as any)
        }));
        domainOffers.incompleted.items = domainOffers.incompleted.items.map((item, idx) => ({
          ...item,
          ...(response.incompleted.items[idx] as any)
        }));
        return domainOffers;
      })
    );
  }

  getOfferDetail(offerId: string): Observable<OfferDetail> {
    const url = `${this.baseUrl}/offers/${offerId}`;
    return this.http.get<OfferDetailDto>(url).pipe(
      map(dto => QuoteMapper.offerDetailToDomain(dto))
    );
  }

  acceptOffer(offerId: string): Observable<OfferDetail> {
    const url = `${this.baseUrl}/offers/${offerId}/accept`;
    return this.http.post<OfferDetailDto>(url, {}).pipe(
      map(dto => QuoteMapper.offerDetailToDomain(dto))
    );
  }

  getSummary(quoteId: string): Observable<QuoteSummaryDto> {
    // Para el summary, devolvemos el DTO directamente porque los componentes
    // esperan la estructura exacta del backend
    const url = `${this.baseUrl}/${quoteId}/summary`;
    return this.http.get<QuoteSummaryDto>(url);
  }

  getWorkTypes(): Observable<WorkType[]> {
    return this.http.get<WorkTypeDto[]>(`${this.baseUrl}/work-types`).pipe(
      map(dtos => WorkTypeMapper.toDomainList(dtos))
    );
  }

  getScheduleOptions(): Observable<ScheduleOption[]> {
    return this.http.get<ScheduleOptionDto[]>(`${this.baseUrl}/schedule-options`).pipe(
      map(dtos => ScheduleOptionMapper.toDomainList(dtos))
    );
  }
}

